/**
 * NarrativeBlock - Display narrator/character dialogue with speaker badges
 */

import { ReactNode } from 'react';

interface NarrativeBlockProps {
  speaker: string;
  content: string | ReactNode;
  speakerColor?: string;
}

const speakerColors: Record<string, string> = {
  NARRATOR: 'bg-[#12356B]/10 text-[#12356B] border-[#12356B]/25',
  MOTHERSHIP: 'bg-[#12356B]/10 text-[#12356B] border-[#CED4DE]',
  GEE: 'bg-[#DD6832]/10 text-[#c4562a] border-[#DD6832]/30',
  DOC: 'bg-[#367A25]/10 text-[#367A25] border-[#367A25]/25',
  BIOLOGIST: 'bg-[#367A25]/10 text-[#367A25] border-[#367A25]/25',
  DIVER: 'bg-[#12356B]/10 text-[#12356B] border-[#12356B]/25',
  FIXER: 'bg-[#DD6832]/10 text-[#c4562a] border-[#DD6832]/30',
  HISTORIAN: 'bg-[#12356B]/10 text-[#12356B] border-[#12356B]/30',
  HUNTER: 'bg-[#DD6832]/10 text-[#c4562a] border-[#DD6832]/30',
};

const speakerIcons: Record<string, string> = {
  NARRATOR: '/icons/centauri-logo.png',
  MOTHERSHIP: '/icons/MOTHERSHIP.png',
  GEE: '/icons/GEE.png',
  DOC: '/icons/doc.png',
  BIOLOGIST: '/icons/biologist.png',
  DIVER: '/icons/diver.png',
  FIXER: '/icons/fixer.png',
  HISTORIAN: '/icons/historian.png',
  HUNTER: '/icons/hunter.png',
};

export function NarrativeBlock({ speaker, content, speakerColor }: NarrativeBlockProps) {
  const colorClass = speakerColor || speakerColors[speaker] || 'bg-[#E2E5EC] text-slate-800 border-[#CED4DE]';
  const iconPath = speakerIcons[speaker];

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        {iconPath && (
          <img src={iconPath} alt={speaker} className="w-6 h-6 object-contain" />
        )}
        <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
          {speaker}
        </div>
      </div>
      <div className="text-sm text-slate-700 leading-relaxed">
        {typeof content === 'string' ? (
          content.split('\n').map((line, i) => (
            <p key={i} className={i > 0 ? 'mt-2' : ''}>
              {line}
            </p>
          ))
        ) : (
          content
        )}
      </div>
    </div>
  );
}
